import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addMinutes, isSameDay } from 'date-fns';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/api';
import EventForm from './EventForm';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarSchedule.css';

const locales = { 'en-US': require('date-fns/locale/en-US') };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales
});

export default function CalendarSchedule({ isAdmin = false, clients = [], currentUserId, onEventCreated }) {
  const { t } = useLanguage();
  const [selectedClientId, setSelectedClientId] = useState(isAdmin ? '' : currentUserId);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlotDate, setSelectedSlotDate] = useState(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState(null); // { date, events[] }

  const fetchTasks = useCallback(async () => {
    if (!isAdmin && !currentUserId) return;
    setLoading(true);
    try {
      const params = {};
      if (isAdmin && selectedClientId) params.clientId = selectedClientId;
      const res = await api.get('/tasks', { params });
      const tasks = res.data.data || [];
      const evts = tasks
        .filter(t => t.status !== 'cancelled')
        .map(t => {
          const d = new Date(t.date);
          const [h, m] = (t.time || '09:00').split(':').map(Number);
          d.setHours(h, m || 0, 0, 0);
          const end = t.endTime
            ? (() => {
                const [eh, em] = t.endTime.split(':').map(Number);
                const e = new Date(d);
                e.setHours(eh, em || 0, 0, 0);
                return e;
              })()
            : addMinutes(d, (t.hours || 1) * 60);
          const cleanerName = t.cleaner?.name;
          return {
            id: t.id,
            title: cleanerName
              ? `${cleanerName} — ${t.title || t.address || t.cleaningType}`
              : (t.title || t.address || t.cleaningType),
            start: d,
            end,
            resource: t
          };
        });
      setEvents(evts);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClientId, currentUserId, isAdmin]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const selectedClientName = isAdmin && selectedClientId
    ? (clients.find(c => (c._id || c.id) == selectedClientId)?.name || '')
    : '';

  const handleEventCreated = () => {
    setShowNewEvent(false);
    fetchTasks();
    if (typeof onEventCreated === 'function') onEventCreated();
  };

  const handleSubmit = async (payload) => {
    const url = isAdmin ? '/tasks/admin/create' : '/tasks';
    await api.post(url, payload);
    handleEventCreated();
  };

  return (
    <div className="calendar-schedule">
      <div className="calendar-schedule-header">
        <div className="calendar-schedule-top">
          {isAdmin ? (
            <div className="calendar-schedule-client-select">
              <label>{t('admin.client', 'Client')}</label>
              <select
                value={selectedClientId || ''}
                onChange={(e) => setSelectedClientId(e.target.value || null)}
                className="form-control"
              >
                <option value="">{t('admin.allClients', 'All clients')}</option>
                {clients.map(c => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.name} {c.email ? `(${c.email})` : ''}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <h2 className="calendar-schedule-title">{t('scheduling.mySchedule', 'My Schedule')}</h2>
          )}
        </div>
      </div>

      <div className="calendar-schedule-body">
        <aside className="calendar-schedule-sidebar calendar-schedule-sidebar-left">
          <button
            type="button"
            className="btn btn-primary calendar-schedule-new-event"
            onClick={() => setShowNewEvent(true)}
          >
            {t('scheduling.newEvent', 'New event')}
          </button>
        </aside>

        <main className="calendar-schedule-main">
          {loading ? (
            <div className="calendar-schedule-loading">{t('common.loading', 'Loading...')}</div>
          ) : (
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              titleAccessor="title"
              views={['month', 'week', 'day']}
              defaultView="month"
              selectable
              style={{ height: 500 }}
              className="calendar-schedule-calendar"
              onSelectSlot={(slotInfo) => {
                const dayEvents = events.filter(evt => isSameDay(evt.start, slotInfo.start));
                if (dayEvents.length > 0) {
                  setSelectedDateEvents({ date: slotInfo.start, events: dayEvents });
                } else {
                  setSelectedSlotDate(slotInfo.start);
                  setShowNewEvent(true);
                }
              }}
              onSelectEvent={(event) => {
                const dayEvents = events.filter(evt => isSameDay(evt.start, event.start));
                setSelectedDateEvents({ date: event.start, events: dayEvents });
              }}
            />
          )}
        </main>

        <aside className="calendar-schedule-sidebar calendar-schedule-sidebar-right">
          <div className="calendar-schedule-client-name">
            <strong>{t('admin.client', 'Client')}</strong>
            <p>{selectedClientId ? selectedClientName : t('admin.allClients', 'All clients')}</p>
          </div>
        </aside>
      </div>

      {showNewEvent && (
        <div className="calendar-schedule-modal-overlay" onClick={() => { setShowNewEvent(false); setSelectedSlotDate(null); }}>
          <div className="calendar-schedule-modal" onClick={e => e.stopPropagation()}>
            <div className="calendar-schedule-modal-header">
              <h3>{t('scheduling.newEvent', 'New event')}</h3>
              <button type="button" className="calendar-schedule-modal-close" onClick={() => { setShowNewEvent(false); setSelectedSlotDate(null); }}>×</button>
            </div>
            <div className="calendar-schedule-modal-body">
              <EventForm
                onSubmit={handleSubmit}
                onCancel={() => { setShowNewEvent(false); setSelectedSlotDate(null); }}
                isAdmin={isAdmin}
                clients={clients}
                initialClientId={selectedClientId}
                initialDate={selectedSlotDate}
              />
            </div>
          </div>
        </div>
      )}

      {selectedDateEvents && (
        <div className="calendar-schedule-modal-overlay" onClick={() => setSelectedDateEvents(null)}>
          <div className="calendar-schedule-modal" onClick={e => e.stopPropagation()}>
            <div className="calendar-schedule-modal-header">
              <h3>{format(selectedDateEvents.date, 'dd/MM/yyyy')} — {selectedDateEvents.events.length} {selectedDateEvents.events.length === 1 ? t('scheduling.event', 'Event') : t('scheduling.events', 'Events')}</h3>
              <button type="button" className="calendar-schedule-modal-close" onClick={() => setSelectedDateEvents(null)}>×</button>
            </div>
            <div className="calendar-schedule-modal-body">
              {selectedDateEvents.events.map((evt, idx) => (
                <div key={evt.id || idx} className="calendar-date-event-card">
                  <h4 className="calendar-date-event-title">{evt.title}</h4>
                  <p className="calendar-date-event-detail">
                    <strong>{t('scheduling.time', 'Time')}:</strong> {format(evt.start, 'HH:mm')} - {format(evt.end, 'HH:mm')}
                  </p>
                  {evt.resource?.address && (
                    <p className="calendar-date-event-detail"><strong>{t('scheduling.address', 'Address')}:</strong> {evt.resource.address}</p>
                  )}
                  {evt.resource?.status && (
                    <p className="calendar-date-event-detail"><strong>{t('scheduling.status', 'Status')}:</strong> {evt.resource.status}</p>
                  )}
                  {evt.resource?.cleaningType && (
                    <p className="calendar-date-event-detail"><strong>{t('scheduling.cleaningType', 'Cleaning Type')}:</strong> {evt.resource.cleaningType}</p>
                  )}
                  {evt.resource?.cleaner ? (
                    <div className="calendar-date-event-cleaner">
                      <strong>{t('scheduling.assignedCleaner', 'Assigned Cleaner')}:</strong>
                      <p className="calendar-date-event-detail">{evt.resource.cleaner.name}</p>
                    </div>
                  ) : (
                    <p className="calendar-date-event-detail" style={{ color: '#f59e0b', fontStyle: 'italic' }}>
                      {t('scheduling.noCleanerAssigned', 'No cleaner assigned yet')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
